import { lastValueFrom } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';

import { PORTAL_API_BASE_URL } from '@echospaces-web-monorepo/es-portal-shell';

export type UpgradeStatus =
  | 'PENDING' | 'APPROVED' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'ROLLED_BACK';

export interface CustomerUpgrade {
  id: string;
  tenantOrganizationId: string;
  fromSchemaVersion: string;
  toSchemaVersion: string;
  apiVersion: string;
  status: UpgradeStatus;
  backupRef: string | null;
  requestedAt: string;
  approvedAt: string | null;
  approvedBy: string | null;
  startedAt: string | null;
  completedAt: string | null;
  errorMessage: string | null;
}

/**
 * At-a-glance schema-version posture for the company overview: where the
 * tenant is now, the latest published release, whether it's behind, and any
 * upgrade already awaiting approval.
 */
export interface TenantVersionStatus {
  currentSchemaVersion: string | null;
  currentVersion: string | null;
  latestSchemaVersion: string | null;
  latestVersion: string | null;
  upToDate: boolean;
  pendingUpgrade: CustomerUpgrade | null;
}

const IN_FLIGHT: ReadonlyArray<UpgradeStatus> = ['APPROVED', 'RUNNING'];
const HISTORY: ReadonlyArray<UpgradeStatus> = ['COMPLETED', 'FAILED', 'ROLLED_BACK'];

/**
 * Time between status polls while an upgrade is in flight. Five seconds
 * is a good trade-off — the orchestrator's transitions are minute-scale
 * (backup, migration, routing flip), so faster polling just spams the
 * BFF. Stops automatically once no rows are in-flight.
 */
const POLL_INTERVAL_MS = 5000;

/**
 * Customer-facing schema-upgrade state. One fetch (`loadAll`) brings in
 * every row for the tenant; three computeds split the list into the UI's
 * sections (in-flight, pending, history). Once anything is in-flight,
 * the service auto-polls each in-flight row's status until it terminates.
 *
 * Slug-in-URL is the routing convention from
 * `[[project_api_self_reference_convention]]` — `us` (org-scoped self).
 */
@Injectable({ providedIn: 'root' })
export class UpgradeService {
  private readonly http = inject(HttpClient);
  private readonly api = inject(PORTAL_API_BASE_URL);

  private readonly _all = signal<CustomerUpgrade[]>([]);
  private readonly _loading = signal(false);
  private readonly _loadError = signal(false);
  private readonly _approving = signal<string | null>(null);
  private readonly _versionStatus = signal<TenantVersionStatus | null>(null);
  private readonly _versionLoading = signal(false);
  private readonly _versionError = signal(false);
  private readonly _initiating = signal(false);

  /** Internal polling handle — `setInterval` id. `null` when idle. */
  private pollTimer: ReturnType<typeof setInterval> | null = null;

  readonly all = this._all.asReadonly();
  readonly loading = this._loading.asReadonly();
  /** True when the last `loadAll` failed — the UI must not pretend "up to date". */
  readonly loadError = this._loadError.asReadonly();
  /** Id of the upgrade currently being approved, or null. */
  readonly approving = this._approving.asReadonly();
  /** Current-vs-latest version posture for the overview card; null until loaded. */
  readonly versionStatus = this._versionStatus.asReadonly();
  readonly versionLoading = this._versionLoading.asReadonly();
  /** True when the last `loadVersionStatus` failed. */
  readonly versionError = this._versionError.asReadonly();
  /** True while an "initiate upgrade" request is in flight. */
  readonly initiating = this._initiating.asReadonly();

  readonly pending = computed<CustomerUpgrade[]>(() =>
    this._all().filter((u) => u.status === 'PENDING'));

  readonly inFlight = computed<CustomerUpgrade[]>(() =>
    this._all().filter((u) => IN_FLIGHT.includes(u.status)));

  readonly history = computed<CustomerUpgrade[]>(() =>
    this._all().filter((u) => HISTORY.includes(u.status)));

  /**
   * Fetch the full per-tenant upgrade list and auto-start polling if
   * anything is in flight.
   */
  async loadAll(): Promise<void> {
    this._loading.set(true);
    this._loadError.set(false);
    try {
      const data = await lastValueFrom(
        this.http.get<CustomerUpgrade[]>(`${this.api}/companies/us/upgrades`),
      );
      this._all.set(data);
      this.syncPolling();
    } catch {
      // Surface the failure so the panel shows an error — NOT a misleading
      // "you're up to date" empty state (which is just `pending().length===0`).
      this._loadError.set(true);
    } finally {
      this._loading.set(false);
    }
  }

  /**
   * Fetch the tenant's current-vs-latest schema version for the company
   * overview card. Cheap, no polling — re-call after `initiate`/`approve`
   * to refresh the badge.
   */
  async loadVersionStatus(): Promise<void> {
    this._versionLoading.set(true);
    this._versionError.set(false);
    try {
      const data = await lastValueFrom(
        this.http.get<TenantVersionStatus>(`${this.api}/companies/us/upgrades/version-status`),
      );
      this._versionStatus.set(data);
    } catch {
      // Drop any stale/optimistic value so the card doesn't keep showing a
      // "pending" badge that the server can't actually confirm.
      this._versionStatus.set(null);
      this._versionError.set(true);
    } finally {
      this._versionLoading.set(false);
    }
  }

  /**
   * Opt in to the latest release — creates a PENDING offer (idempotent on
   * the server). Does NOT run the upgrade; the super-admin still approves it
   * in the Settings panel, which is what triggers the backup + migration.
   * Refreshes the version status so the overview flips to "pending".
   */
  async initiate(): Promise<CustomerUpgrade> {
    this._initiating.set(true);
    try {
      const offer = await lastValueFrom(
        this.http.post<CustomerUpgrade>(`${this.api}/companies/us/upgrades/initiate`, {}),
      );
      // Merge into the list (so the Settings panel shows it without a reload)
      // and reflect the new pending offer in the overview badge.
      this._all.update((list) => (list.some((u) => u.id === offer.id) ? list : [offer, ...list]));
      this._versionStatus.update((s) => (s ? { ...s, pendingUpgrade: offer } : s));
      return offer;
    } finally {
      this._initiating.set(false);
    }
  }

  async approve(upgradeId: string): Promise<CustomerUpgrade> {
    this._approving.set(upgradeId);
    try {
      const result = await lastValueFrom(
        this.http.post<CustomerUpgrade>(`${this.api}/upgrades/${upgradeId}/approve`, {}),
      );
      // Patch the row in place — server transitioned PENDING → APPROVED;
      // polling will take over from here.
      this._all.update((list) => list.map((u) => (u.id === upgradeId ? result : u)));
      this.syncPolling();
      return result;
    } finally {
      this._approving.set(null);
    }
  }

  async getStatus(upgradeId: string): Promise<CustomerUpgrade> {
    return lastValueFrom(
      this.http.get<CustomerUpgrade>(`${this.api}/upgrades/${upgradeId}`),
    );
  }

  /**
   * Start the poll loop if at least one upgrade is in-flight AND we
   * aren't already polling. Stop it when there's nothing left to watch.
   * Safe to call repeatedly — idempotent.
   */
  syncPolling(): void {
    const shouldPoll = this.inFlight().length > 0;
    if (shouldPoll && !this.pollTimer) {
      this.pollTimer = setInterval(() => this.pollInFlight(), POLL_INTERVAL_MS);
    } else if (!shouldPoll && this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  /**
   * Imperatively halt the poll loop. Useful in tests + for consumers
   * that know they're going away (the service is `providedIn: 'root'`
   * so it survives navigation; explicit stop avoids stale fetches).
   */
  stopPolling(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  /**
   * Re-fetch each in-flight row's status individually. Per-row beats a
   * full re-list because the customer typically has 1–2 in-flight at a
   * time but tens of history rows; re-fetching the history is wasted
   * bandwidth.
   */
  private async pollInFlight(): Promise<void> {
    const inFlightIds = this.inFlight().map((u) => u.id);
    if (inFlightIds.length === 0) {
      this.syncPolling();
      return;
    }
    const results = await Promise.allSettled(
      inFlightIds.map((id) => this.getStatus(id)),
    );
    this._all.update((list) => {
      let next = list;
      for (const r of results) {
        if (r.status === 'fulfilled') {
          next = next.map((u) => (u.id === r.value.id ? r.value : u));
        }
      }
      return next;
    });
    this.syncPolling();
  }
}
